import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (password !== confirm) { toast.error("Le due password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password aggiornata! Ora puoi accedere.");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 noise-bg">
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <SiteLogo className="w-9 h-9" />
          <div className="flex flex-col leading-tight">
            <BrandText />
          </div>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-[#0F172A] p-8" data-testid="reset-password-card">
          <h1 className="font-display text-3xl flex items-center gap-2"><KeyRound className="w-6 h-6 text-gold" /> Nuova password</h1>
          {!token ? (
            <p className="text-sm text-destructive mt-3" data-testid="reset-no-token">Link non valido. Torna al login e richiedi un nuovo link di reset.</p>
          ) : done ? (
            <div className="mt-6 inline-flex items-center gap-2 text-gold" data-testid="reset-success"><CheckCircle2 className="w-5 h-5" /> Password aggiornata! Reindirizzamento al login…</div>
          ) : (
            <>
              <p className="text-muted-foreground text-sm mt-1">Scegli una nuova password per il tuo account.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="pwd">Nuova password</Label>
                  <Input id="pwd" data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="Almeno 6 caratteri" />
                </div>
                <div>
                  <Label htmlFor="pwd2">Conferma password</Label>
                  <Input id="pwd2" data-testid="reset-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5" placeholder="Ripeti la password" />
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reimposta password"}
                </Button>
              </form>
            </>
          )}
          <p className="text-sm text-muted-foreground mt-6 text-center">
            <Link to="/login" className="text-gold hover:underline">Torna al login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
